import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CalendarDays, LogOut, Star, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const stars = (rating) => (
  <span className="stars">{"★★★★★".split("").map((s, i) => <span key={i} className={i < Math.round(rating) ? "star-on" : "star-off"}>{s}</span>)}</span>
);

export default function Admin() {
  const [user, setUser] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("bookings");
  const [bookings, setBookings] = useState([]);
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    axios.get(`${API}/auth/me`, { withCredentials: true }).then((r) => setUser(r.data)).catch(() => setUser(false));
  }, []);

  const loadData = useCallback(async () => {
    try {
      const [b, r] = await Promise.all([
        axios.get(`${API}/admin/bookings`, { withCredentials: true }),
        axios.get(`${API}/admin/reviews`, { withCredentials: true }),
      ]);
      setBookings(b.data);
      setReviews(r.data);
    } catch {
      toast.error("Could not load studio data.");
    }
  }, []);

  useEffect(() => { if (user) loadData(); }, [user, loadData]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      setUser(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
    setUser(false);
    setEmail("");
    setPassword("");
  };

  const deleteReview = async (id) => {
    try {
      await axios.delete(`${API}/admin/reviews/${id}`, { withCredentials: true });
      setReviews(reviews.filter((r) => r.id !== id));
      toast.success("Review removed.");
    } catch {
      toast.error("Could not remove the review.");
    }
  };

  if (user === null) return <div className="admin-loading" data-testid="admin-loading">Loading…</div>;

  if (!user) {
    return (
      <div className="admin-login-wrap" data-testid="admin-login-view">
        <div className="grain" aria-hidden="true" />
        <motion.form className="admin-login" onSubmit={login} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}>
          <svg width="38" height="38" viewBox="0 0 64 64" aria-hidden="true">
            <rect width="64" height="64" rx="16" fill="#14121a" stroke="rgba(212,175,55,.45)" />
            <path d="M32 12l5.2 14.8L52 32l-14.8 5.2L32 52l-5.2-14.8L12 32l14.8-5.2z" fill="#D4AF37" />
          </svg>
          <h1>Owner Studio</h1>
          <p>Sign in to manage bookings and reviews.</p>
          {error && <div className="admin-error" data-testid="admin-login-error">{error}</div>}
          <input type="email" required placeholder="Email" data-testid="admin-email-input" value={email} onChange={(e) => setEmail(e.target.value)} />
          <input type="password" required placeholder="Password" data-testid="admin-password-input" value={password} onChange={(e) => setPassword(e.target.value)} />
          <button className="gold-button full" data-testid="admin-login-submit" type="submit" disabled={busy}>{busy ? "Signing in…" : "Sign in"}</button>
          <a href="/" className="admin-back" data-testid="admin-back-link">← Back to profile</a>
        </motion.form>
      </div>
    );
  }

  return (
    <div className="admin" data-testid="admin-dashboard">
      <div className="grain" aria-hidden="true" />
      <header className="admin-head">
        <div>
          <span className="section-kicker">Owner Studio</span>
          <h1>Namaste, {user.name?.split(" ")[0] || "Jyoti"}</h1>
        </div>
        <button className="ghost-button sm" data-testid="admin-logout-button" onClick={logout}><LogOut size={14} /> Sign out</button>
      </header>
      <div className="admin-tabs">
        <button className={tab === "bookings" ? "active" : ""} data-testid="admin-tab-bookings" onClick={() => setTab("bookings")}><CalendarDays size={15} /> Bookings <small>{bookings.length}</small></button>
        <button className={tab === "reviews" ? "active" : ""} data-testid="admin-tab-reviews" onClick={() => setTab("reviews")}><Star size={15} /> Reviews <small>{reviews.length}</small></button>
      </div>

      {tab === "bookings" && (
        <div className="admin-list" data-testid="admin-bookings-list">
          {bookings.length === 0 && <p className="admin-empty" data-testid="admin-empty-bookings">No booking requests yet. New requests from the profile page will appear here.</p>}
          {bookings.map((b) => (
            <article className="admin-row" key={b.id} data-testid="admin-booking-item">
              <div className="admin-row-main">
                <b>{b.name}</b>
                <span className="admin-tag">{b.reading_type}</span>
              </div>
              <div className="admin-row-meta">
                <span>{b.date} · {b.time}</span>
                <span>{b.contact}</span>
              </div>
              {b.note && <p className="admin-note">“{b.note}”</p>}
            </article>
          ))}
        </div>
      )}

      {tab === "reviews" && (
        <div className="admin-list" data-testid="admin-reviews-list">
          {reviews.length === 0 && <p className="admin-empty" data-testid="admin-empty-reviews">No reviews yet.</p>}
          {reviews.map((r) => (
            <article className="admin-row" key={r.id} data-testid="admin-review-item">
              <div className="admin-row-main">
                <b>{r.name}</b>
                {stars(r.rating)}
                <span className="admin-date">{r.created_at.slice(0, 10)}</span>
                <button className="admin-delete" data-testid={`admin-review-delete-${r.id}`} onClick={() => deleteReview(r.id)} aria-label="Delete review"><Trash2 size={15} /></button>
              </div>
              <p className="admin-note">{r.text}</p>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
